import { useFormContext } from "react-hook-form";
import {
  ArrayInput,
  SimpleFormIterator,
  ReferenceInput,
  AutocompleteInput,
  required,
  SelectInput,
  TextInput,
  FormDataConsumer,
} from "react-admin";
import { Grid } from "@mui/material";

import CustomDateInput from "server/components/Fields/CustomDateInput";
import StudyGroupField from "server/components/Fields/StudyGroupField";
import { CourseChoices } from "utils/choices";
import { validateEnd, validateStart } from "utils/validations";
import { studentRequests } from "api/studentApi";

const CyclicScheduleForm = ({ mode }) => {
  const STORE_KEY = "RaStore.cyclicSchedule.filters";
  const storedValue = JSON.parse(localStorage.getItem(STORE_KEY));
  const isEdit = mode === "edit";
  const defaultValues = {
    academicYearId: storedValue?.academicYearId || "",
    course: storedValue?.course || "",
    curriculumId: storedValue?.curriculumId || "",
    instructionLanguageId: storedValue?.instructionLanguageId || "",
    specialityId: storedValue?.specialityId || "",
    structuralUnitId: storedValue?.structuralUnitId || "",
  };

  const { watch } = useFormContext();
  const currentCourse = watch("course");
  const curriculumId = isEdit ? defaultValues.curriculumId : watch("curriculumId");

  return (
    <>
      <Grid
        container
        spacing={2}
        px={6}
      >
        <Grid
          item
          xs={12}
          md={5}
        >
          <ReferenceInput
            source="academicYearId"
            reference={studentRequests.getAcademicYearsSelect()}
          >
            <SelectInput
              defaultValue={defaultValues.academicYearId || null}
              label="Академична година"
              helperText={false}
              validate={required()}
              disabled={isEdit}
            />
          </ReferenceInput>
        </Grid>
        <Grid
          item
          xs={12}
          md={5}
        >
          <ReferenceInput
            source="curriculumId"
            reference={studentRequests.getCurriculumsSelect()}
            filter={{ specialityId: defaultValues.specialityId }}
          >
            <AutocompleteInput
              defaultValue={defaultValues.curriculumId}
              filterToQuery={q => (q?.length > 2 ? { name: q } : {})}
              label="Учебен план"
              optionText="name"
              disabled={isEdit}
              fullWidth
            />
          </ReferenceInput>
        </Grid>
        {curriculumId ? (
          <Grid
            item
            xs={12}
            md={5}
          >
            <ReferenceInput
              source="instructionLanguageId"
              reference={studentRequests.getCurriculumLanguagesSelect(curriculumId)}
            >
              <SelectInput
                optionText="name"
                optionValue="id"
                label="Език на обучение"
                defaultValue={defaultValues.instructionLanguageId}
                emptyText={false}
                fullWidth
              />
            </ReferenceInput>
          </Grid>
        ) : null}
        <Grid
          item
          xs={12}
          md={5}
        >
          <SelectInput
            source="course"
            label="Курс"
            choices={CourseChoices}
            optionValue="id"
            optionText="name"
            emptyText={false}
            defaultValue={defaultValues.course}
          />
        </Grid>

        <Grid
          item
          xs={12}
          md={5}
        >
          <TextInput
            label="Поток"
            source="name"
          />
        </Grid>
        <Grid
          item
          xs={12}
          md={5}
        >
          <StudyGroupField
            labelStyle={{ fontSize: "11px" }}
            label="Пример за въвеждане на Учебни групи: 1-5, 8, 11-13, 15. Оставете празно ако разписанието се отнася за всички
        групи!"
          />
        </Grid>
      </Grid>
      <ArrayInput
        label="Дисциплини"
        source="disciplines"
        defaultValue={[{}]}
      >
        <SimpleFormIterator>
          <Grid
            container
            spacing={2}
          >
            {curriculumId ? (
              <Grid
                item
                xs={12}
              >
                <ReferenceInput
                  source="disciplineId"
                  reference={studentRequests.getCurriculumCyclicDisciplinesSelect(curriculumId)}
                  filter={{ course: currentCourse }}
                >
                  <AutocompleteInput
                    validate={required()}
                    filterToQuery={q => (q?.length > 2 ? { disciplineName: q } : {})}
                    optionText="name"
                    label="Дисциплина"
                    fullWidth
                  />
                </ReferenceInput>
              </Grid>
            ) : null}
            <FormDataConsumer>
              {({ formData, scopedFormData }) => (
                <>
                  <Grid
                    item
                    xs={12}
                    md={6}
                  >
                    <CustomDateInput
                      source="classesStartDate"
                      label="Начало на занятия"
                      validate={() =>
                        validateStart(scopedFormData.classesStartDate, scopedFormData.classesEndDate, required)
                      }
                      required
                      noTime
                    />
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    md={6}
                  >
                    <CustomDateInput
                      source="classesEndDate"
                      label="Край на занятия"
                      noTime
                      required
                      validate={() =>
                        validateEnd(scopedFormData.classesEndDate, scopedFormData.classesStartDate, required)
                      }
                    />
                  </Grid>{" "}
                  <Grid
                    item
                    xs={12}
                    md={6}
                  >
                    <CustomDateInput
                      source="examsStartDate"
                      label="Начало на сесия"
                      validate={() =>
                        validateStart(
                          scopedFormData.examsStartDate,
                          scopedFormData.examsEndDate,
                          false,
                          "Началната дата на сесията трябва да е преди крайната",
                          scopedFormData?.classesEndDate,
                          "Началната дата на сесията трябва да е след край на занятия",
                        )
                      }
                      noTime
                    />
                  </Grid>{" "}
                  <Grid
                    item
                    xs={12}
                    md={6}
                  >
                    <CustomDateInput
                      source="examsEndDate"
                      label="Край на сесия"
                      validate={() =>
                        validateEnd(
                          scopedFormData.examsEndDate,
                          scopedFormData.examsStartDate,
                          false,
                          "Крайната дата на сесията трябва да е след началната",
                        )
                      }
                      noTime
                    />
                  </Grid>
                </>
              )}
            </FormDataConsumer>
          </Grid>
        </SimpleFormIterator>
      </ArrayInput>
    </>
  );
};

export default CyclicScheduleForm;
